import React, { useState, useEffect } from "react";
import {
  Search,
  Calendar,
  Clock,
  Trash2,
  MessageSquare,
  Send,
  AlertCircle,
  CheckCircle,
  Plus,
  X,
  Edit,
  FileText,
  Upload,
} from "lucide-react";
import { useTheme } from "../../contexts/ThemeContext";
import { API_BASE_URL } from "../../config";
import Layout from "../Layout/Layout";
import Loader from "../utils/Loader";
import ReactPaginate from "react-paginate";
import { formatDateOnly } from "../utils/formatDate";
import TrimText from "../utils/TrimText";
import WhatsAppLivePreview from "../shared/WhatsAppLivePreview.jsx";

const RecurringMessagePage = () => {
  const { theme } = useTheme();
  const [receiverMobileNo, setReceiverMobileNo] = useState("");
  const [message, setMessage] = useState("");
  const [scheduledDate, setScheduledDate] = useState("");
  const [time, setTime] = useState("");
  const [files, setFiles] = useState([]);
  const [recurringMessages, setRecurringMessages] = useState([]);
  const [editingMessageId, setEditingMessageId] = useState(null);
  const [templateName, setTemplateName] = useState("");
  const [templateContent, setTemplateContent] = useState("");
  const [showDropdown, setShowDropdown] = useState(false);
  const [templates, setTemplates] = useState([]);
  const [filteredMessages, setFilteredMessages] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [notifications, setNotifications] = useState([]);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [showMobilePreview, setShowMobilePreview] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const messagesPerPage = 10;
  // Add state for input focus if not present
  const [inputFocused, setInputFocused] = useState(false);

  const showNotification = (message, type = "success") => {
    const id = Date.now();
    const notification = { id, message, type };
    setNotifications((prev) => [...prev, notification]);
    setTimeout(() => {
      setNotifications((prev) => prev.filter((n) => n.id !== id));
    }, 4000);
  };

  useEffect(() => {
    const fetchTemplates = async () => {
      setLoading(true);
      const token = window.__appSession.getToken();
      try {
        const response = await fetch(`${API_BASE_URL}/api/templates`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const data = await response.json();
        setTemplates(data);
      } catch (error) {
        console.error("Error fetching templates:", error);
        showNotification("Failed to fetch templates.", "error");
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  const fetchRecurringMessages = async () => {
    setLoading(true);
    const token = window.__appSession.getToken();
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/whatsapp/recurring-messages`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const data = await response.json();
      setRecurringMessages(data);
      setFilteredMessages(data);
    } catch (error) {
      console.error("Error fetching recurring messages:", error);
      showNotification("Failed to fetch recurring messages.", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchRecurringMessages();
  }, []);

  const handleTemplateSelect = async (name) => {
    setTemplateName(name);
    setShowDropdown(false);
    await fetchTemplate(name);
  };

  const fetchTemplate = async (name) => {
    const token = window.__appSession.getToken();
    try {
      const response = await fetch(
        `${API_BASE_URL}/api/templates/name/${name}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        },
      );
      const data = await response.json();
      const { text, imageUrls } = data;
      setTemplateContent(text);
      setFiles(imageUrls || []);
    } catch (error) {
      console.error("Error fetching template:", error);
      showNotification("Failed to fetch template.", "error");
    }
  };

  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);
    setCurrentPage(0); // Reset to first page on search
    if (query === "") {
      setFilteredMessages(recurringMessages);
    } else {
      const search = query.toLowerCase();
      const filtered = recurringMessages.filter(
        (msg) =>
          (msg.receiverMobileNo &&
            msg.receiverMobileNo.toLowerCase().includes(search)) ||
          (msg.message && msg.message.toLowerCase().includes(search)) ||
          (msg.scheduledDate &&
            new Date(msg.scheduledDate)
              .toLocaleDateString()
              .toLowerCase()
              .includes(search)) ||
          (msg.time && msg.time.toLowerCase().includes(search)) ||
          (msg.filePathUrl &&
            Array.isArray(msg.filePathUrl) &&
            msg.filePathUrl.join(",").toLowerCase().includes(search)) ||
          (msg._id && msg._id.toLowerCase().includes(search)),
      );
      setFilteredMessages(filtered);
    }
  };

  const handleScheduleRecurringMessage = async (e) => {
    e.preventDefault();
    setLoading(true);

    if (!receiverMobileNo || !scheduledDate || !time) {
      showNotification("Please fill in all fields.", "error");
      setLoading(false);
      return;
    }

    let fileUrls = [];
    if (files.length > 0) {
      const formData = new FormData();
      files.forEach((file) => formData.append("files", file));
      try {
        const token = window.__appSession.getToken();
        const uploadResponse = await fetch(
          `${API_BASE_URL}/api/whatsapp/upload`,
          {
            method: "POST",
            headers: { Authorization: `Bearer ${token}` },
            body: formData,
          },
        );
        const uploadData = await uploadResponse.json();
        fileUrls = uploadData.filePathUrls;
      } catch (error) {
        console.error("Error uploading files:", error);
        showNotification("Failed to upload files.", "error");
        setLoading(false);
        return;
      }
    }

    let messageToSend = message;
    if (templateName) {
      const token = window.__appSession.getToken();
      try {
        const response = await fetch(
          `${API_BASE_URL}/api/templates/name/${templateName}`,
          {
            headers: { Authorization: `Bearer ${token}` },
          },
        );
        const data = await response.json();
        const { text } = data;
        messageToSend = text;
      } catch (error) {
        console.error("Error fetching template:", error);
        showNotification("Failed to fetch template.", "error");
        setLoading(false);
        return;
      }
    } else if (!messageToSend.trim()) {
      showNotification(
        "Please provide a message or select a template.",
        "error",
      );
      setLoading(false);
      return;
    }

    const localDate = new Date(`${scheduledDate}T${time}:00`);
    const utcScheduledDateTime = localDate.toISOString();

    const messageData = {
      receiverMobileNo,
      message: messageToSend,
      scheduledDate: utcScheduledDateTime,
      time,
      filePathUrl: fileUrls,
    };

    try {
      const token = window.__appSession.getToken();
      if (editingMessageId) {
        await fetch(
          `${API_BASE_URL}/api/whatsapp/recurring-messages/${editingMessageId}`,
          {
            method: "PUT",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${token}`,
            },
            body: JSON.stringify(messageData),
          },
        );
        showNotification("Recurring message updated successfully!", "success");
      } else {
        await fetch(`${API_BASE_URL}/api/whatsapp/schedule-recurring-message`, {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(messageData),
        });
        showNotification(
          "Recurring message scheduled successfully!",
          "success",
        );
      }
      resetForm();
      setIsModalOpen(false);
      fetchRecurringMessages();
    } catch (error) {
      console.error("Error scheduling/updating recurring message:", error);
      showNotification("Failed to schedule/update recurring message.", "error");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setReceiverMobileNo("");
    setMessage("");
    setScheduledDate("");
    setTime("");
    setFiles([]);
    setEditingMessageId(null);
    setTemplateName("");
    setTemplateContent("");
  };

  const handleEditRecurringMessage = (msg) => {
    setReceiverMobileNo(msg.receiverMobileNo);
    setMessage(msg.message);
    setScheduledDate(new Date(msg.scheduledDate).toISOString().split("T")[0]);
    setTime(msg.time);
    setFiles([]);
    setEditingMessageId(msg._id);
    setIsModalOpen(true);
  };

  const handleDeleteRecurringMessage = async (id) => {
    const token = window.__appSession.getToken();
    try {
      await fetch(`${API_BASE_URL}/api/whatsapp/recurring-messages/${id}`, {
        method: "DELETE",
        headers: { Authorization: `Bearer ${token}` },
      });
      showNotification("Recurring message deleted successfully.", "success");
      fetchRecurringMessages();
    } catch (error) {
      console.error("Error deleting recurring message:", error);
      showNotification("Failed to delete recurring message.", "error");
    }
  };

  const MAX_SIZE = 25 * 1024 * 1024;

  const handleFileChange = (e) => {
    const selectedFiles = Array.from(e.target.files);
    const validFiles = selectedFiles.filter((file) => {
      if (file.size > MAX_SIZE) {
        setError("File size exceeds the 25MB limit.");
        return false;
      }
      return true;
    });
    setFiles(validFiles);
    setError("");
  };

  const generatePreview = (file) => {
    // If file is a File object (from input)
    if (file instanceof File) {
      const fileType = file.type.split("/")[0];
      const previewUrl = URL.createObjectURL(file);

      if (fileType === "image") {
        return (
          <img
            src={previewUrl}
            alt={file.name}
            className="w-12 h-12 object-cover rounded-lg"
          />
        );
      }
      if (fileType === "application" && file.name.endsWith(".pdf")) {
        return (
          <div className="w-12 h-12 flex items-center justify-center bg-gray-100 rounded-lg">
            <FileText className="text-gray-500 text-lg" />
          </div>
        );
      }
      return null;
    }
    // If file is a string (URL)
    if (typeof file === "string") {
      // You can add more logic here if you want to handle PDFs differently
      return (
        <img
          src={file}
          alt="Attached"
          className="w-12 h-12 object-cover rounded-lg"
        />
      );
    }
    return null;
  };

  // Pagination logic
  const pageCount = Math.ceil(filteredMessages.length / messagesPerPage);
  const offset = currentPage * messagesPerPage;
  const currentMessages = filteredMessages.slice(
    offset,
    offset + messagesPerPage,
  );

  const handlePageChange = ({ selected }) => {
    setCurrentPage(selected);
  };

  // Filter templates based on input
  const filteredTemplates = templateName
    ? templates.filter((template) =>
        template.templateName
          .toLowerCase()
          .includes(templateName.toLowerCase()),
      )
    : templates;

  return (
    <>
      <Layout>
        <div>
          {/* Notifications */}
          <div className="fixed top-4 right-4 z-50 space-y-2">
            {notifications.map((notification) => (
              <div
                key={notification.id}
                className={`rounded-xl border px-3 py-2 text-xs font-medium shadow-lg backdrop-blur ${
                  notification.type === "success"
                    ? "border-emerald-200 bg-emerald-500/95 text-white"
                    : notification.type === "error"
                      ? "border-rose-200 bg-rose-500/95 text-white"
                      : "border-indigo-200 bg-indigo-500/95 text-white"
                }`}
              >
                {notification.message}
              </div>
            ))}
          </div>

          {loading && <Loader theme={theme} text="Loading..." />}

          <div className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-[max-content_1fr] gap-3 mb-3">
              {/* Header */}
              <div
                className={`glass-surface-strong ${theme.contentBg} ${theme.contentBorder} border rounded-2xl px-3 py-2`}
              >
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center space-x-2">
                    <div className="rounded-xl bg-gradient-to-br from-indigo-600 to-violet-600 p-2 shadow-[0_14px_30px_-22px_rgba(79,70,229,0.9)]">
                      <Calendar className="h-4 w-4 text-white" />
                    </div>
                    <div>
                      <h1 className="text-base font-semibold text-slate-900">
                        Recurring Messages
                      </h1>
                      <p className="text-xs text-slate-500">
                        Manage your recurring WhatsApp messages
                      </p>
                    </div>
                  </div>

                  {/* Stats */}
                  <div className="flex items-center space-x-3">
                    <div className="text-center">
                      <div className="flex items-center space-x-1">
                        <MessageSquare className="h-3 w-3 text-indigo-500" />
                        <span className="text-xs font-medium text-slate-900">
                          {recurringMessages.length}
                        </span>
                      </div>
                      <p className="text-xs text-slate-500">Total</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Controls */}
              <div
                className={`glass-surface ${theme.contentBg} ${theme.contentBorder} border rounded-2xl flex w-full items-center px-3 py-2`}
              >
                <div className="flex flex-col sm:flex-row gap-3 w-full items-start sm:items-center justify-between">
                  {/* Search */}
                  <div className="relative flex-1 w-full ">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-3 w-3 text-slate-400" />
                    <input
                      type="text"
                      value={searchQuery}
                      onChange={handleSearchChange}
                      placeholder="Search messages..."
                      className={`w-full rounded-xl border border-indigo-200 bg-white/85 pl-9 pr-3 py-2 text-xs text-slate-700 focus:outline-none focus:ring-2 ${theme.inputFocusRing}`}
                    />
                  </div>

                  {/* Action Button */}
                  <button
                    onClick={() => setIsModalOpen(true)}
                    className="flex items-center space-x-2 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-3 py-2 text-xs font-medium text-white transition hover:opacity-95"
                  >
                    <Plus className="h-3 w-3" />
                    <span>New Recurring Message</span>
                  </button>
                </div>
              </div>
            </div>

            {/* Messages Table */}
            <div
              className={`glass-surface ${theme.contentBg} ${theme.contentBorder} border rounded-2xl overflow-hidden`}
            >
              <div className="p-3 border-b border-indigo-200">
                <h3 className="text-sm font-semibold text-slate-900 flex items-center">
                  <MessageSquare className="h-4 w-4 mr-2" />
                  Recurring Messages ({filteredMessages.length})
                </h3>
              </div>

              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gradient-to-r from-indigo-100/90 via-violet-100/90 to-indigo-100/90 top-0 sticky z-10">
                    <tr>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                        #
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                        Scheduled Date
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                        Mobile Number
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                        Time
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                        Message
                      </th>

                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                        Files
                      </th>
                      <th className="px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-slate-700">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-indigo-100/80">
                    {currentMessages.length === 0 ? (
                      <tr>
                        <td colSpan={10} className="px-3 py-6 text-center">
                          <AlertCircle className="h-8 w-8 text-slate-400 mx-auto mb-2" />
                          <p className="text-xs text-slate-500">
                            No recurring messages found
                          </p>
                        </td>
                      </tr>
                    ) : (
                      currentMessages.map((msg, idx) => (
                        <tr
                          key={msg._id}
                          className="transition-colors hover:bg-indigo-50/55"
                        >
                          <td className="px-3 py-1 text-xs text-slate-700 max-w-xs">
                            {idx + offset + 1}
                          </td>
                          <td className="px-3 py-1 text-xs text-slate-700">
                            {formatDateOnly(msg.scheduledDate)}
                          </td>
                          <td className="px-3 py-1 text-xs font-medium text-slate-900">
                            {msg.receiverMobileNo}
                          </td>
                          <td className="px-3 py-1 text-xs text-slate-700">
                            {msg.time}
                          </td>

                          <td className="px-3 py-1 text-xs text-slate-700 max-w-xs">
                            <div className="" title={msg.message}>
                              {<TrimText text={msg.message} maxLen={60} />}
                            </div>
                          </td>

                          <td className="px-3 py-1">
                            {msg.filePathUrl && msg.filePathUrl.length > 0 ? (
                              <div className="flex space-x-1">
                                {msg.filePathUrl
                                  .slice(0, 2)
                                  .map((url, index) => (
                                    <img
                                      key={index}
                                      src={url}
                                      alt="Attached"
                                      className="w-6 h-6 object-cover rounded"
                                    />
                                  ))}
                                {msg.filePathUrl.length > 2 && (
                                  <span className="text-xs text-slate-500">
                                    +{msg.filePathUrl.length - 2}
                                  </span>
                                )}
                              </div>
                            ) : (
                              <span className="text-xs text-slate-400">
                                No files
                              </span>
                            )}
                          </td>
                          <td className="px-3 py-1">
                            <div className="flex space-x-1">
                              <button
                                onClick={() => handleEditRecurringMessage(msg)}
                                className="rounded-md p-1 text-indigo-500 transition-colors hover:bg-indigo-50 hover:text-indigo-700"
                              >
                                <Edit className="h-3 w-3" />
                              </button>
                              <button
                                onClick={() =>
                                  handleDeleteRecurringMessage(msg._id)
                                }
                                className="rounded-md p-1 text-rose-500 transition-colors hover:bg-rose-50 hover:text-rose-700"
                              >
                                <Trash2 className="h-3 w-3" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {/* Table Footer */}
              <div className="border-t border-indigo-200 bg-indigo-50/40 px-3 py-2">
                <div className="flex items-center justify-between">
                  <div className="text-xs text-slate-700">
                    Showing {currentMessages.length} of{" "}
                    {filteredMessages.length} messages
                  </div>
                  <div className="text-xs text-slate-500">
                    Total: {recurringMessages.length} recurring messages
                  </div>
                  {/* Pagination */}
                  {pageCount > 1 && (
                    <div>
                      <ReactPaginate
                        previousLabel={"Prev"}
                        nextLabel={"Next"}
                        breakLabel={"..."}
                        pageCount={pageCount}
                        marginPagesDisplayed={2}
                        pageRangeDisplayed={5}
                        onPageChange={handlePageChange}
                        containerClassName={
                          "flex items-center justify-center space-x-2 text-xs"
                        }
                        pageClassName={"rounded-lg border border-indigo-200 bg-white px-3 py-1 hover:bg-indigo-50"}
                        pageLinkClassName={"text-slate-700"}
                        previousClassName={
                          "rounded-lg border border-indigo-200 bg-white px-3 py-1 hover:bg-indigo-50"
                        }
                        previousLinkClassName={"text-slate-700"}
                        nextClassName={"rounded-lg border border-indigo-200 bg-white px-3 py-1 hover:bg-indigo-50"}
                        nextLinkClassName={"text-slate-700"}
                        breakClassName={"px-3 py-1"}
                        breakLinkClassName={"text-slate-700"}
                        activeClassName={"!border-indigo-600 !bg-indigo-600 text-white"}
                        disabledClassName={"opacity-50 cursor-not-allowed"}
                      />
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </Layout>
      {/* Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/45 p-4">
          {loading && <Loader theme={theme} text="Processing..." />}
          <div
            className={`glass-surface-strong ${theme.contentBg} w-full max-w-5xl max-h-[90vh] overflow-y-auto rounded-2xl border border-indigo-200/70 shadow-xl`}
            style={{ scrollbarWidth: "thin" }}
          >
            <div className="flex items-center justify-between border-b border-indigo-200 px-4 py-3">
              <h3 className="text-sm font-semibold text-slate-900 flex items-center">
                <Calendar className="h-4 w-4 mr-2" />
                {editingMessageId
                  ? "Edit Recurring Message"
                  : "Schedule Recurring Message"}
              </h3>
              <button
                onClick={() => {
                  setIsModalOpen(false);
                  resetForm();
                }}
                className="rounded-lg border border-indigo-200 p-1 text-slate-500 transition-colors hover:bg-indigo-50 hover:text-slate-700"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-[1fr_320px] gap-4 p-4">
              <form
                onSubmit={handleScheduleRecurringMessage}
                className="space-y-3"
              >
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Mobile Number
                  </label>
                  <input
                    type="tel"
                    value={receiverMobileNo}
                    onChange={(e) => setReceiverMobileNo(e.target.value)}
                    placeholder="+1234567890"
                    className={`w-full rounded-xl border border-indigo-200 bg-white/90 px-3 py-2 text-xs text-slate-700 focus:outline-none focus:ring-2 ${theme.inputFocusRing}`}
                    required
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Message
                  </label>
                  <textarea
                    value={message}
                    onChange={(e) => setMessage(e.target.value)}
                    placeholder="Enter your message..."
                    rows={3}
                    className={`w-full resize-none rounded-xl border border-indigo-200 bg-white/90 px-3 py-2 text-xs text-slate-700 focus:outline-none focus:ring-2 ${theme.inputFocusRing}`}
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Template (Optional)
                  </label>
                  <div className="relative">
                    <input
                      type="text"
                      value={templateName}
                      onChange={(e) => {
                        e.stopPropagation();
                        setTemplateName(e.target.value);
                        setShowDropdown(true);
                      }}
                      placeholder="Select or type template name"
                      className={`w-full rounded-xl border border-indigo-200 bg-white/90 px-3 py-2 text-xs text-slate-700 focus:outline-none focus:ring-2 ${theme.inputFocusRing}`}
                      onFocus={() => {
                        setInputFocused(true);
                        setShowDropdown(true);
                      }}
                      onBlur={() => {
                        setTimeout(() => {
                          setInputFocused(false);
                          setShowDropdown(false);
                        }, 150);
                        if (templateName) fetchTemplate(templateName);
                      }}
                    />
                    <button
                      type="button"
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowDropdown((prev) => !prev);
                      }}
                      className="absolute right-3 top-1/2 transform -translate-y-1/2 text-slate-400"
                      tabIndex={-1}
                    >
                      <Search className="h-3 w-3" />
                    </button>
                    {showDropdown && filteredTemplates.length > 0 && (
                      <div className="absolute z-10 mt-1 max-h-32 w-full overflow-y-auto rounded-xl border border-indigo-200 bg-white shadow-lg">
                        {filteredTemplates.map((template) => (
                          <button
                            key={template._id}
                            type="button"
                            onMouseDown={(e) => {
                              e.stopPropagation();
                              handleTemplateSelect(template.templateName);
                              setShowDropdown(false);
                            }}
                            className="w-full p-2 text-left text-xs text-slate-700 hover:bg-indigo-50"
                          >
                            {template.templateName}
                          </button>
                        ))}
                      </div>
                    )}
                  </div>
                  {templateContent && (
                    <div className="mt-2 rounded-lg border border-indigo-200 bg-indigo-50/60 p-2">
                      <p className="text-xs text-slate-700">{templateContent}</p>
                    </div>
                  )}
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      Date
                    </label>
                    <input
                      type="date"
                      value={scheduledDate}
                      onChange={(e) => setScheduledDate(e.target.value)}
                      className={`w-full rounded-xl border border-indigo-200 bg-white/90 px-3 py-2 text-xs text-slate-700 focus:outline-none focus:ring-2 ${theme.inputFocusRing}`}
                      required
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1">
                      Time
                    </label>
                    <input
                      type="time"
                      value={time}
                      onChange={(e) => setTime(e.target.value)}
                      className={`w-full rounded-xl border border-indigo-200 bg-white/90 px-3 py-2 text-xs text-slate-700 focus:outline-none focus:ring-2 ${theme.inputFocusRing}`}
                      required
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1">
                    Files (Optional)
                  </label>
                  <div className="rounded-xl border-2 border-dashed border-indigo-200 bg-white/75 p-3 text-center">
                    <input
                      type="file"
                      multiple
                      accept="image/*,application/pdf"
                      onChange={handleFileChange}
                      className="hidden"
                      id="file-upload"
                    />
                    <label htmlFor="file-upload" className="cursor-pointer">
                      <Upload className="mx-auto mb-1 h-6 w-6 text-slate-400" />
                      <p className="text-xs text-slate-600">
                        Click to upload files (Images, PDFs). Max 25MB each.
                      </p>
                    </label>
                  </div>
                  {error && <p className="text-red-600 text-xs mt-1">{error}</p>}
                  {files.length > 0 && (
                    <div className="mt-2 flex gap-2 flex-wrap">
                      {files.map((file, index) => (
                        <div key={index} className="relative group">
                          {generatePreview(file)}
                          <p className="text-center text-xs mt-1  max-w-[48px]">
                            {file instanceof File ? file.name : ""}
                          </p>
                        </div>
                      ))}
                    </div>
                  )}
                </div>

                <div className="flex space-x-2 pt-2">
                  <button
                    type="button"
                    onClick={() => setShowMobilePreview(true)}
                    className="md:hidden flex-1 rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-2 text-xs font-medium text-slate-700 transition-colors hover:bg-indigo-100"
                  >
                    Preview
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setIsModalOpen(false);
                      resetForm();
                    }}
                    className="flex-1 rounded-xl border border-indigo-200 bg-white px-4 py-2 text-xs font-medium text-slate-700 transition-colors hover:bg-indigo-50"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    disabled={loading}
                    className="flex flex-1 items-center justify-center space-x-2 rounded-xl bg-gradient-to-r from-indigo-600 to-violet-600 px-4 py-2 text-xs font-medium text-white transition-opacity hover:opacity-95 disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <Calendar className="h-3 w-3" />
                    <span>{editingMessageId ? "Update" : "Schedule"}</span>
                  </button>
                </div>
              </form>

              <div className="hidden md:block">
                <WhatsAppLivePreview
                  title="Recurring Preview"
                  receiverMobileNo={receiverMobileNo}
                  message={templateContent || message}
                  scheduleTime={
                    scheduledDate && time ? `${scheduledDate}T${time}` : ""
                  }
                  attachments={files}
                />
              </div>
            </div>

            {showMobilePreview && (
              <div className="fixed inset-0 z-50 md:hidden">
                <div
                  className="absolute inset-0 bg-black/45"
                  onClick={() => setShowMobilePreview(false)}
                />
                <div className="absolute bottom-0 left-0 right-0 max-h-[78vh] overflow-y-auto rounded-t-2xl border border-indigo-200 bg-white/95 p-3 shadow-2xl backdrop-blur">
                  <div className="mb-2 flex items-center justify-between">
                    <h4 className="text-sm font-semibold text-slate-800">Live Preview</h4>
                    <button
                      type="button"
                      onClick={() => setShowMobilePreview(false)}
                      className="rounded border border-indigo-200 px-2 py-1 text-xs text-slate-700"
                    >
                      Close
                    </button>
                  </div>
                  <WhatsAppLivePreview
                    title="Recurring Preview"
                    receiverMobileNo={receiverMobileNo}
                    message={templateContent || message}
                    scheduleTime={
                      scheduledDate && time ? `${scheduledDate}T${time}` : ""
                    }
                    attachments={files}
                  />
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default RecurringMessagePage;

// import React, { useState, useEffect } from "react";
// import axios from "axios";
// import { API_BASE_URL } from "../../config";
// import { ToastContainer, toast } from "react-toastify";
// import "react-toastify/dist/ReactToastify.css";
// import { motion } from "framer-motion"; // For animations
// import { FaTrash, FaFileAlt } from "react-icons/fa";
// import { useDropzone } from "react-dropzone";
// import Layout from "../Layout/Layout"; // Import the enhanced Layout component
// import "./style.css";
// import "../../App.css";

// const Footer = () => {
//   return (
//     <footer className="bg-gradient-to-r from-green-600 to-teal-500 text-white py-4 text-center shadow-inner">
//       <p className="text-sm tracking-wide">
//         Â© {new Date().getFullYear()} Smart ItBox. All rights reserved.
//       </p>
//     </footer>
//   );
// };

// const RecurringMessagePage = () => {
//   const [receiverMobileNo, setReceiverMobileNo] = useState("");
//   const [message, setMessage] = useState("");
//   const [scheduledDate, setScheduledDate] = useState("");
//   const [time, setTime] = useState("");
//   const [files, setFiles] = useState([]);
//   const [recurringMessages, setRecurringMessages] = useState([]);
//   const [editingMessageId, setEditingMessageId] = useState(null);
//   const [templateName, setTemplateName] = useState("");
//   const [templateContent, setTemplateContent] = useState("");
//   const [showDropdown, setShowDropdown] = useState(false);
//   const [templates, setTemplates] = useState([]);
//   const [filteredMessages, setFilteredMessages] = useState([]);
//   const [searchQuery, setSearchQuery] = useState("");
//   const [loading, setLoading] = useState(false);
//   const [error, setError] = useState("");

//   useEffect(() => {
//     const fetchTemplates = async () => {
//       setLoading(true);
//       const token = window.__appSession.getToken();
//       try {
//         const response = await axios.get(`${API_BASE_URL}/api/templates`, {
//           headers: { Authorization: `Bearer ${token}` },
//         });
//         setTemplates(response.data);
//       } catch (error) {
//         console.error("Error fetching templates:", error);
//         toast.error("Failed to fetch templates.");
//       } finally {
//         setLoading(false);
//       }
//     };

//     fetchTemplates();
//   }, []);

//   const fetchRecurringMessages = async () => {
//     setLoading(true);
//     const token = window.__appSession.getToken();
//     try {
//       const response = await axios.get(
//         `${API_BASE_URL}/api/whatsapp/recurring-messages`,
//         {
//           headers: { Authorization: `Bearer ${token}` },
//         }
//       );
//       setRecurringMessages(response.data);
//       setFilteredMessages(response.data);
//     } catch (error) {
//       console.error("Error fetching recurring messages:", error);
//       toast.error("Failed to fetch recurring messages.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   useEffect(() => {
//     fetchRecurringMessages();
//   }, []);

//   const handleTemplateSelect = async (name) => {
//     setTemplateName(name);
//     setShowDropdown(false);
//     await fetchTemplate(name);
//   };

//   const fetchTemplate = async (name) => {
//     const token = window.__appSession.getToken();
//     try {
//       const response = await axios.get(
//         `${API_BASE_URL}/api/templates/name/${name}`,
//         {
//           headers: { Authorization: `Bearer ${token}` },
//         }
//       );
//       const { text, imageUrls } = response.data;
//       setTemplateContent(text);
//       setFiles(imageUrls || []);
//     } catch (error) {
//       console.error("Error fetching template:", error);
//       toast.error("Failed to fetch template.");
//     }
//   };

//   const handleSearchChange = (e) => {
//     const query = e.target.value;
//     setSearchQuery(query);
//     if (query === "") {
//       setFilteredMessages(recurringMessages);
//     } else {
//       const filtered = recurringMessages.filter((msg) =>
//         msg.receiverMobileNo.includes(query)
//       );
//       setFilteredMessages(filtered);
//     }
//   };

//   const handleScheduleRecurringMessage = async (e) => {
//     e.preventDefault();
//     setLoading(true);

//     if (!receiverMobileNo || !scheduledDate || !time) {
//       toast.error("Please fill in all fields.");
//       setLoading(false);
//       return;
//     }

//     let fileUrls = [];
//     if (files.length > 0) {
//       const formData = new FormData();
//       files.forEach((file) => formData.append("files", file));
//       try {
//         const token = window.__appSession.getToken();
//         const uploadResponse = await axios.post(
//           `${API_BASE_URL}/api/whatsapp/upload`,
//           formData,
//           {
//             headers: {
//               "Content-Type": "multipart/form-data",
//               Authorization: `Bearer ${token}`,
//             },
//           }
//         );
//         fileUrls = uploadResponse.data.filePathUrls;
//       } catch (error) {
//         console.error("Error uploading files:", error);
//         toast.error("Failed to upload files.");
//         setLoading(false);
//         return;
//       }
//     }

//     let messageToSend = message;
//     if (templateName) {
//       const token = window.__appSession.getToken();
//       try {
//         const response = await axios.get(
//           `${API_BASE_URL}/api/templates/name/${templateName}`,
//           {
//             headers: { Authorization: `Bearer ${token}` },
//           }
//         );
//         const { text } = response.data;
//         messageToSend = text;
//       } catch (error) {
//         console.error("Error fetching template:", error);
//         toast.error("Failed to fetch template.");
//         setLoading(false);
//         return;
//       }
//     } else if (!messageToSend.trim()) {
//       toast.error("Please provide a message or select a template.");
//       setLoading(false);
//       return;
//     }

//     const localDate = new Date(`${scheduledDate}T${time}:00`);
//     const utcScheduledDateTime = localDate.toISOString();

//     const messageData = {
//       receiverMobileNo,
//       message: messageToSend,
//       scheduledDate: utcScheduledDateTime,
//       time,
//       filePathUrl: fileUrls,
//     };

//     try {
//       const token = window.__appSession.getToken();
//       if (editingMessageId) {
//         await axios.put(
//           `${API_BASE_URL}/api/whatsapp/recurring-messages/${editingMessageId}`,
//           messageData,
//           { headers: { Authorization: `Bearer ${token}` } }
//         );
//         toast.success("Recurring message updated successfully!");
//       } else {
//         await axios.post(
//           `${API_BASE_URL}/api/whatsapp/schedule-recurring-message`,
//           messageData,
//           { headers: { Authorization: `Bearer ${token}` } }
//         );
//         toast.success("Recurring message scheduled successfully!");
//       }
//       resetForm();
//       fetchRecurringMessages();
//     } catch (error) {
//       console.error("Error scheduling/updating recurring message:", error);
//       toast.error("Failed to schedule/update recurring message.");
//     } finally {
//       setLoading(false);
//     }
//   };

//   const resetForm = () => {
//     setReceiverMobileNo("");
//     setMessage("");
//     setScheduledDate("");
//     setTime("");
//     setFiles([]);
//     setEditingMessageId(null);
//     setTemplateName("");
//     setTemplateContent("");
//   };

//   const handleEditRecurringMessage = (msg) => {
//     setReceiverMobileNo(msg.receiverMobileNo);
//     setMessage(msg.message);
//     setScheduledDate(new Date(msg.scheduledDate).toISOString().split("T")[0]);
//     setTime(msg.time);
//     setFiles([]);
//     setEditingMessageId(msg._id);
//   };

//   const handleDeleteRecurringMessage = async (id) => {
//     const token = window.__appSession.getToken();
//     try {
//       await axios.delete(
//         `${API_BASE_URL}/api/whatsapp/recurring-messages/${id}`,
//         {
//           headers: { Authorization: `Bearer ${token}` },
//         }
//       );
//       toast.success("Recurring message deleted successfully.");
//       fetchRecurringMessages();
//     } catch (error) {
//       console.error("Error deleting recurring message:", error);
//       toast.error("Failed to delete recurring message.");
//     }
//   };

//   const MAX_SIZE = 25 * 1024 * 1024;

//   const { getRootProps, getInputProps } = useDropzone({
//     onDrop: (acceptedFiles, rejectedFiles) => {
//       if (rejectedFiles.length > 0) {
//         setError("File size exceeds the 25MB limit or invalid file type.");
//       } else {
//         setFiles(acceptedFiles);
//         setError("");
//       }
//     },
//     accept: "image/*,application/pdf",
//     maxSize: MAX_SIZE,
//   });

//   const generatePreview = (file) => {
//     const fileType = file.type.split("/")[0];
//     const previewUrl = URL.createObjectURL(file);

//     if (fileType === "image") {
//       return (
//         <img
//           src={previewUrl}
//           alt={file.name}
//           className="w-20 h-20 object-cover rounded-lg"
//         />
//       );
//     }
//     if (fileType === "video") {
//       return (
//         <video
//           src={previewUrl}
//           controls
//           className="w-20 h-20 object-cover rounded-lg"
//         >
//           Your browser does not support the video tag.
//         </video>
//       );
//     }
//     if (fileType === "application" && file.name.endsWith(".pdf")) {
//       return (
//         <div className="w-20 h-20 flex items-center justify-center bg-gray-100 rounded-lg">
//           <FaFileAlt className="text-gray-500 text-2xl" />
//         </div>
//       );
//     }
//     return null;
//   };

//   return (
//     <Layout>
//       <motion.main
//         initial={{ opacity: 0 }}
//         animate={{ opacity: 1 }}
//         transition={{ duration: 0.8 }}
//         className="flex-grow w-full min-h-[calc(100vh-4rem)] py-6 px-4 sm:px-6"
//       >
//         {loading && (
//           <motion.div
//             className="absolute inset-0 bg-gray-900 bg-opacity-50 flex items-center justify-center z-50"
//             initial={{ opacity: 0 }}
//             animate={{ opacity: 1 }}
//             exit={{ opacity: 0 }}
//           >
//             <svg
//               className="animate-spin h-12 w-12 text-[#25D366]"
//               xmlns="http://www.w3.org/2000/svg"
//               viewBox="0 0 24 24"
//             >
//               <circle
//                 className="opacity-25"
//                 cx="12"
//                 cy="12"
//                 r="10"
//                 stroke="currentColor"
//                 strokeWidth="4"
//               />
//               <path
//                 className="opacity-75"
//                 fill="currentColor"
//                 d="M4 12a8 8 0 1 1 16 0A8 8 0 0 1 4 12z"
//               />
//             </svg>
//           </motion.div>
//         )}

//         <div className="bg-white/90 backdrop-blur-md shadow-2xl rounded-2xl p-6 sm:p-8 w-full max-w-6xl mx-auto border border-gray-200 transition-all">
//           <h1 className="text-3xl sm:text-4xl font-bold text-center mb-6 text-[#25D366] tracking-tight font-serif">
//             Recurring WhatsApp Messages
//           </h1>

//           <div className="flex flex-col md:flex-row gap-6">
//             {/* Form Section */}
//             <div className="w-full md:w-1/2">
//               <form
//                 onSubmit={handleScheduleRecurringMessage}
//                 className="flex flex-col space-y-4"
//               >
//                 <input
//                   type="tel"
//                   placeholder="Receiver Mobile No"
//                   value={receiverMobileNo}
//                   onChange={(e) => setReceiverMobileNo(e.target.value)}
//                   required
//                   className="w-full p-3 text-sm bg-gray-50/80 backdrop-blur-sm border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#25D366] transition-all"
//                 />
//                 <textarea
//                   placeholder="Enter message"
//                   value={message}
//                   onChange={(e) => setMessage(e.target.value)}
//                   className="w-full p-3 text-sm bg-gray-50/80 backdrop-blur-sm border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#25D366] transition-all"
//                   rows="4"
//                 />
//                 <div className="relative">
//                   <input
//                     type="text"
//                     placeholder="Template Name"
//                     value={templateName}
//                     onChange={(e) => setTemplateName(e.target.value)}
//                     className="w-full p-3 text-sm bg-gray-50/80 backdrop-blur-sm border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#25D366] transition-all"
//                     onBlur={() => fetchTemplate(templateName)}
//                   />
//                   <button
//                     type="button"
//                     onClick={() => setShowDropdown(!showDropdown)}
//                     className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-500"
//                   >
//                     â–¼
//                   </button>
//                   {showDropdown && (
//                     <ul className="absolute bg-white border border-gray-200 w-full mt-1 rounded-lg shadow-lg z-10 max-h-40 overflow-y-auto">
//                       {templates.map((template) => (
//                         <li
//                           key={template._id}
//                           onClick={() =>
//                             handleTemplateSelect(template.templateName)
//                           }
//                           className="p-2 hover:bg-gray-100 cursor-pointer text-sm"
//                         >
//                           {template.templateName}
//                         </li>
//                       ))}
//                     </ul>
//                   )}
//                   {templateContent && (
//                     <div className="mt-4 p-4 bg-gray-50 rounded-lg">
//                       <p className="text-sm text-gray-700">{templateContent}</p>
//                       <div className="mt-2 flex space-x-4">
//                         {files.map((url, index) => (
//                           <img
//                             key={index}
//                             src={url}
//                             alt="Template"
//                             className="w-16 h-16 object-cover rounded-md"
//                           />
//                         ))}
//                       </div>
//                     </div>
//                   )}
//                 </div>
//                 <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
//                   <input
//                     type="date"
//                     value={scheduledDate}
//                     onChange={(e) => setScheduledDate(e.target.value)}
//                     required
//                     className="w-full p-3 text-sm bg-gray-50/80 backdrop-blur-sm border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#25D366] transition-all"
//                   />
//                   <input
//                     type="time"
//                     value={time}
//                     onChange={(e) => setTime(e.target.value)}
//                     required
//                     className="w-full p-3 text-sm bg-gray-50/80 backdrop-blur-sm border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#25D366] transition-all"
//                   />
//                 </div>

//                 {/* Drag-and-Drop File Input */}
//                 <motion.div
//                   whileHover={{ scale: 1.01 }}
//                   {...getRootProps()}
//                   className="border-2 border-dashed border-gray-300 rounded-xl p-6 text-center text-gray-600 hover:bg-gray-50 transition-all"
//                 >
//                   <input {...getInputProps()} />
//                   <p className="text-sm font-medium">
//                     Drag and drop your files here, or click to select (Images,
//                     PDFs). Max size: 25MB.
//                   </p>
//                 </motion.div>
//                 {error && (
//                   <p className="text-red-600 text-center mt-2 text-sm">
//                     {error}
//                   </p>
//                 )}
//                 {files.length > 0 && (
//                   <div className="mt-4 flex gap-4 flex-wrap">
//                     {files.map((file, index) => (
//                       <div key={index} className="relative group">
//                         {generatePreview(file)}
//                         <p className="text-center text-xs mt-2  max-w-[80px]">
//                           {file.name}
//                         </p>
//                       </div>
//                     ))}
//                   </div>
//                 )}

//                 <motion.button
//                   whileHover={{
//                     scale: 1.05,
//                     boxShadow: "0 8px 25px rgba(37, 211, 102, 0.3)",
//                   }}
//                   whileTap={{ scale: 0.95 }}
//                   type="submit"
//                   className="w-full bg-gradient-to-r from-[#25D366] to-[#128C7E] text-white py-3 rounded-lg font-semibold shadow-md hover:shadow-xl transition-all mt-4"
//                 >
//                   {editingMessageId ? "Update Message" : "Schedule Message"}
//                 </motion.button>
//               </form>
//             </div>

//             {/* Recurring Messages Section */}
//             <div className="w-full md:w-1/2">
//               <h2 className="text-xl sm:text-2xl font-semibold mb-4 text-gray-800 tracking-wide">
//                 Your Recurring Messages
//               </h2>

//               {/* Search Input */}
//               <motion.div
//                 whileFocus={{ scale: 1.02 }}
//                 className="relative mb-6"
//               >
//                 <input
//                   type="text"
//                   placeholder="Search by Mobile No"
//                   value={searchQuery}
//                   onChange={handleSearchChange}
//                   className="w-full p-3 text-sm bg-gray-50/80 backdrop-blur-sm border border-gray-200 rounded-lg shadow-sm focus:outline-none focus:ring-2 focus:ring-[#25D366] transition-all"
//                 />
//                 <svg
//                   className="absolute right-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-500"
//                   xmlns="http://www.w3.org/2000/svg"
//                   fill="none"
//                   viewBox="0 0 24 24"
//                   stroke="currentColor"
//                 >
//                   <path
//                     strokeLinecap="round"
//                     strokeLinejoin="round"
//                     strokeWidth="2"
//                     d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"
//                   />
//                 </svg>
//               </motion.div>

//               {/* Total Messages Count */}
//               <h3 className="text-lg font-semibold mb-4 text-gray-700">
//                 Total Messages: {filteredMessages.length}
//               </h3>

//               <div className="space-y-4 max-h-[600px] overflow-y-auto">
//                 {filteredMessages.length === 0 ? (
//                   <p className="text-gray-600 text-center">
//                     No recurring messages found.
//                   </p>
//                 ) : (
//                   filteredMessages.map((msg) => (
//                     <motion.div
//                       key={msg._id}
//                       initial={{ opacity: 0, y: 10 }}
//                       animate={{ opacity: 1, y: 0 }}
//                       transition={{ duration: 0.3 }}
//                       className="border border-gray-200 p-4 bg-gray-50 rounded-lg flex flex-col sm:flex-row justify-between items-start sm:items-center shadow-sm hover:scale-[1.01] hover:shadow-md transition-all"
//                     >
//                       <div className="flex-1 mb-4 sm:mb-0">
//                         <p className="text-sm font-medium text-gray-700">
//                           Receiver: {msg.receiverMobileNo}
//                         </p>
//                         <p className="text-sm text-gray-600">
//                           Message: {msg.message}
//                         </p>
//                         <p className="text-sm text-gray-600">
//                           Scheduled Date:{" "}
//                           {new Date(msg.scheduledDate).toLocaleDateString()}
//                         </p>
//                         <p className="text-sm text-gray-600">
//                           Time: {msg.time}
//                         </p>
//                         {msg.filePathUrl && msg.filePathUrl.length > 0 && (
//                           <div className="mt-2 flex space-x-2">
//                             {msg.filePathUrl.map((url, index) => (
//                               <img
//                                 key={index}
//                                 src={url}
//                                 alt="Attached"
//                                 className="w-16 h-16 object-cover rounded-md"
//                               />
//                             ))}
//                           </div>
//                         )}
//                       </div>
//                       <div className="flex space-x-2">
//                         <motion.button
//                           whileHover={{ scale: 1.05 }}
//                           whileTap={{ scale: 0.95 }}
//                           onClick={() => handleEditRecurringMessage(msg)}
//                           className="bg-primary-500 text-white py-1 px-3 rounded-lg shadow-md hover:bg-primary-600 transition-all text-sm"
//                         >
//                           Edit
//                         </motion.button>
//                         <motion.button
//                           whileHover={{
//                             scale: 1.05,
//                             boxShadow: "0 8px 25px rgba(239, 68, 68, 0.3)",
//                           }}
//                           whileTap={{ scale: 0.95 }}
//                           onClick={() => handleDeleteRecurringMessage(msg._id)}
//                           className="bg-red-500 text-white py-1 px-3 rounded-lg shadow-md hover:bg-red-600 transition-all text-sm"
//                         >
//                           Delete
//                         </motion.button>
//                       </div>
//                     </motion.div>
//                   ))
//                 )}
//               </div>
//             </div>
//           </div>
//         </div>

//         <ToastContainer />
//       </motion.main>
//     </Layout>
//   );
// };

// export default RecurringMessagePage;
